/**
 * Collection Share Modal
 * Modal for sharing collections with groups and creating guest links
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  shareCollectionWithGroup,
  unshareCollectionFromGroup,
  updateCollectionSharingPermission,
  createGuestLink,
  getGuestLinks,
  deleteGuestLink,
  renewGuestLink,
  toggleMarketplaceVisible,
  getCollectionSharedGroups,
  initiateCollectionTransfer,
  getCollectionTransfer,
  cancelCollectionTransfer,
  GuestLink,
  CollectionSharedGroup,
  CollectionTransfer
} from '../services/collectionSharingService';
import { getUserGroups, SharingGroup } from '../services/sharingService';
import BottomSheet from './BottomSheet';
import '../styles/components/collectionShareModal.css'; // Self-contained modal styles

interface CollectionShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  collection: {
    id: number;
    name: string;
    description?: string;
    collection_type?: string;
    marketplace_visible?: boolean;
  };
  onSuccess?: () => void;
}

const CollectionShareModal: React.FC<CollectionShareModalProps> = ({
  isOpen,
  onClose,
  collection,
  onSuccess
}) => {
  const { token } = useAuth();
  const [view, setView] = useState<'main' | 'share-group' | 'create-link' | 'transfer'>('main');
  const [groups, setGroups] = useState<SharingGroup[]>([]);
  const [sharedGroups, setSharedGroups] = useState<CollectionSharedGroup[]>([]);
  const [guestLinks, setGuestLinks] = useState<GuestLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // Group sharing
  const [selectedGroup, setSelectedGroup] = useState<SharingGroup | null>(null);
  const [sharePermission, setSharePermission] = useState<'view' | 'edit' | 'full_edit'>('view');
  const [editingPermission, setEditingPermission] = useState<{groupId: number, permission: 'view' | 'edit' | 'full_edit'} | null>(null);
  
  // Guest link creation
  const isMarketplace = collection.collection_type === 'marketplace';
  const [linkPermission, setLinkPermission] = useState<'view' | 'edit' | 'full_edit'>(isMarketplace ? 'view' : 'edit');
  const [linkExpires, setLinkExpires] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [maxUses, setMaxUses] = useState<number | null>(null);
  const [requireEmailVerification, setRequireEmailVerification] = useState(false);
  const [createdLink, setCreatedLink] = useState<string | null>(null);

  // Marketplace visibility (collection-level)
  const [marketplaceVisible, setMarketplaceVisible] = useState(false);

  // Collection transfer
  const [transferEmail, setTransferEmail] = useState('');
  const [transferKeepCopy, setTransferKeepCopy] = useState(true);
  const [transferMessage, setTransferMessage] = useState('');
  const [pendingTransfer, setPendingTransfer] = useState<CollectionTransfer | null>(null);
  const [transferDone, setTransferDone] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setLinkPermission(isMarketplace ? 'view' : 'edit');
      setMarketplaceVisible(!!collection.marketplace_visible);
      setTransferEmail('');
      setTransferMessage('');
      setTransferDone(false);
      loadData();
    }
  }, [isOpen, isMarketplace, collection.marketplace_visible]);

  const loadData = async (isRetry = false): Promise<void> => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      const [userGroups, links, shared] = await Promise.all([
        getUserGroups(token),
        getGuestLinks(token, collection.id),
        getCollectionSharedGroups(token, collection.id)
      ]);
      setGroups(userGroups);
      setGuestLinks(links);
      setSharedGroups(shared);
      setRetryCount(0); // Reset retry count on success
    } catch (err: any) {
      // Auto-retry once for network errors
      if (!isRetry && retryCount === 0) {
        setRetryCount(1);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
        return loadData(true);
      }
      
      // Show user-friendly error message
      const errorMessage = err.message || 'Unknown error';
      if (errorMessage.includes('SSL') || errorMessage.includes('EOF') || errorMessage.includes('network')) {
        setError('connection');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShareWithGroup = async () => {
    if (!token || !selectedGroup) return;
    
    try {
      setLoading(true);
      setError(null);
      await shareCollectionWithGroup(token, collection.id, {
        group_id: selectedGroup.id,
        permission: sharePermission
      });
      
      if (onSuccess) onSuccess();
      await loadData();
      setSelectedGroup(null);
    } catch (err: any) {
      setError(err.message || 'Failed to share collection');
    } finally {
      setLoading(false);
    }
  };

  const handleUnshareFromGroup = async (groupId: number) => {
    if (!token) return;
    
    if (!window.confirm('Remove this collection from the group?')) return;
    
    try {
      setLoading(true);
      await unshareCollectionFromGroup(token, collection.id, groupId);
      if (onSuccess) onSuccess();
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to unshare collection');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePermission = async (groupId: number, newPermission: 'view' | 'edit' | 'full_edit') => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      await updateCollectionSharingPermission(token, collection.id, groupId, newPermission);
      if (onSuccess) onSuccess();
      await loadData();
      setEditingPermission(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update permission');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGuestLink = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const result = await createGuestLink(token, collection.id, {
        permission: linkPermission,
        expires_at: linkExpires && expiryDate ? new Date(expiryDate).toISOString() : undefined,
        max_uses: maxUses || undefined,
        require_email_verification: requireEmailVerification
      });
      
      setCreatedLink(result.share_url);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to create guest link');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLink = async (linkId: number) => {
    if (!token) return;
    
    if (!window.confirm('Delete this guest link? This cannot be undone.')) return;
    
    try {
      setLoading(true);
      await deleteGuestLink(token, collection.id, linkId);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete link');
    } finally {
      setLoading(false);
    }
  };

  const handleRenewLink = async (linkId: number) => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      await renewGuestLink(token, collection.id, linkId);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to renew link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Link copied to clipboard!');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleTogglePublish = async () => {
    if (!token) return;
    const newValue = !marketplaceVisible;
    try {
      setLoading(true);
      setError(null);
      await toggleMarketplaceVisible(token, collection.id, newValue);
      setMarketplaceVisible(newValue);
    } catch (err: any) {
      setError(err.message || 'Failed to update marketplace visibility');
    } finally {
      setLoading(false);
    }
  };

  const handleInitiateTransfer = async () => {
    if (!token || !transferEmail.trim()) return;
    try {
      setLoading(true);
      setError(null);
      await initiateCollectionTransfer(
        token,
        collection.id,
        transferEmail.trim(),
        transferKeepCopy,
        transferMessage.trim() || undefined
      );
      setTransferDone(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send transfer invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTransfer = async () => {
    if (!token) return;
    if (!window.confirm('Cancel this transfer invitation?')) return;
    try {
      setLoading(true);
      await cancelCollectionTransfer(token, collection.id);
      setPendingTransfer(null);
      setTransferDone(false);
    } catch (err: any) {
      setError(err.message || 'Failed to cancel transfer');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTransferView = async () => {
    if (!token) return;
    setView('transfer');
    try {
      const result = await getCollectionTransfer(token, collection.id);
      setPendingTransfer(result.transfer);
    } catch {
      setPendingTransfer(null);
    }
  };

  const getTitle = () => {
    if (view === 'create-link') return 'Create Guest Link';
    if (view === 'share-group') return 'Share with Group';
    if (view === 'transfer') return 'Transfer to Client';
    return isMarketplace ? 'Publish' : 'Share';
  };

  return (
    <BottomSheet 
      isOpen={isOpen} 
      onClose={onClose}
      title={getTitle()}
    >
      <div className="collection-share-modal-content">
        {/* Back button for sub-views */}
        {view !== 'main' && (
          <button className="back-btn-inline" onClick={() => setView('main')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            Back
          </button>
        )}

        {/* Marketplace visibility — publish section (at top) */}
        {view === 'main' && isMarketplace && (
          <div style={{
            padding: '12px 0',
            borderBottom: '1px solid var(--color-border)',
          }}>
            {marketplaceVisible ? (
              <a
                href={`https://wanderingsock.com/marketplace/${collection.id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'block',
                  fontSize: '16px',
                  color: 'var(--color-text-link, #2563eb)',
                  textDecoration: 'none',
                  textAlign: 'center',
                  marginBottom: '10px',
                }}
              >
                wanderingsock.com/marketplace/{collection.id}
              </a>
            ) : (
              <span style={{
                display: 'block',
                fontSize: '16px',
                color: 'var(--color-text-disabled, #c0c0c0)',
                textAlign: 'center',
                marginBottom: '10px',
              }}>
                wanderingsock.com/marketplace/{collection.id}
              </span>
            )}
            <button
              onClick={handleTogglePublish}
              disabled={loading}
              className="action-btn primary"
            >
              {loading ? '...' : marketplaceVisible ? 'Unpublish' : 'Publish'}
            </button>
          </div>
        )}

        {/* Action Buttons - Always visible on main view */}
        {view === 'main' && (
          <div className="modal-actions">
            <button
              onClick={() => setView('share-group')}
              className="action-btn primary"
              disabled={groups.length === 0}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              Share with Group
            </button>
            <button
              onClick={() => setView('create-link')}
              className="action-btn primary"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
              </svg>
              Create Guest Link
            </button>
          </div>
        )}

        {error && (
          <div className="error-message">
            {error === 'connection' ? (
              <>
                <div className="error-text">
                  <strong>Connection issue</strong>
                  <p>We couldn't connect to the server. This usually resolves itself quickly.</p>
                </div>
                <button 
                  onClick={() => loadData()} 
                  className="retry-btn"
                  disabled={loading}
                >
                  {loading ? 'Retrying...' : 'Try Again'}
                </button>
              </>
            ) : (
              <>
                <div className="error-text">
                  <strong>Something went wrong</strong>
                  <p>{error}</p>
                </div>
                <button 
                  onClick={() => loadData()} 
                  className="retry-btn"
                  disabled={loading}
                >
                  {loading ? 'Retrying...' : 'Try Again'}
                </button>
              </>
            )}
          </div>
        )}

        {/* Main View — groups & guest links */}
        {view === 'main' && (
          <div className="modal-body">
            {/* Currently Shared Groups */}
            {sharedGroups.length > 0 && (
              <section className="share-section">
                <h3>Shared with Groups</h3>
                <div className="shared-groups-list">
                      {sharedGroups.map(group => (
                          <div key={group.id} className="shared-group-item">
                            <div className="shared-group-info">
                              <span className="group-name">{group.name}</span>
                              {editingPermission?.groupId === group.id ? (
                                <div className="permission-editor">
                                  <select
                                    value={editingPermission.permission}
                                    onChange={(e) => setEditingPermission({
                                      groupId: group.id,
                                      permission: e.target.value as 'view' | 'edit' | 'full_edit'
                                    })}
                                    autoFocus
                                  >
                                    <option value="view">View only</option>
                                    <option value="edit">Can contribute</option>
                                    <option value="full_edit">Full access</option>
                                  </select>
                                  <button
                                    onClick={() => handleUpdatePermission(group.id, editingPermission.permission)}
                                    className="save-permission-btn"
                                    disabled={loading}
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingPermission(null)}
                                    className="cancel-permission-btn"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="permission-actions">
                                  <button
                                    onClick={() => setEditingPermission({
                                      groupId: group.id,
                                      permission: sharePermission
                                    })}
                                    className="permission-badge clickable"
                                  >
                                    {group.permission}
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleUnshareFromGroup(group.id)}
                                    className="unshare-btn"
                                  >
                                    Remove
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                </div>
              </section>
            )}

            {/* Guest Links Section */}
            <section className="share-section">
              <h3>Guest Links</h3>
              <p className="section-description">
                Share with anyone via link - no account required
              </p>

              {guestLinks.length === 0 ? (
                <div className="empty-state">
                  <p>No guest links created yet</p>
                </div>
              ) : (
                <div className="guest-links-list">
                  {guestLinks.map(link => {
                    const now = Date.now();
                    const expiryTime = link.expires_at ? new Date(link.expires_at).getTime() : null;
                    const isExpired = expiryTime && expiryTime < now;
                    const isExpiringSoon = expiryTime && !isExpired && (expiryTime - now) < 24 * 60 * 60 * 1000;
                    const isNearMaxUses = link.max_uses && link.use_count >= link.max_uses * 0.8;
                    const usagePercent = link.max_uses ? (link.use_count / link.max_uses) * 100 : 0;
                    
                    const linkUrl = `${window.location.origin}/shared/${link.share_token}`;
                    
                    return (
                    <div key={link.id} className={`guest-link-item ${!link.is_active ? 'revoked' : ''} ${isExpired ? 'expired' : isExpiringSoon ? 'expiring-soon' : ''}`}>
                      <div className="link-info">
                        <div className="link-header">
                          <span className={`permission-badge ${link.permission === 'view' ? 'view' : link.permission === 'full_edit' ? 'full-edit' : 'edit'}`}>
                            {link.permission === 'view' ? '👁️ View only' : link.permission === 'full_edit' ? '🔓 Full access' : '✏️ Can contribute'}
                          </span>
                          {!link.is_active && <span className="revoked-badge">❌ Revoked</span>}
                          {link.is_active && isExpired && <span className="expired-badge">⏰ Expired</span>}
                          {link.is_active && isExpiringSoon && <span className="warning-badge">⏰ Expiring soon</span>}
                          {link.is_active && isNearMaxUses && <span className="warning-badge">⚠️ Near limit</span>}
                        </div>

                        {/* Clickable Link URL */}
                        {link.is_active && (
                          <div className="link-url" onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(linkUrl);
                          }}>
                            <span className="url-text">{linkUrl}</span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                          </div>
                        )}
                        
                        {/* Usage Progress Bar */}
                        {link.max_uses && link.is_active && (
                          <div className="usage-progress">
                            <div className="progress-bar">
                              <div 
                                className="progress-fill" 
                                style={{ 
                                  width: `${Math.min(usagePercent, 100)}%`,
                                  backgroundColor: usagePercent >= 100 ? 'var(--color-danger)' : usagePercent >= 80 ? 'var(--color-warning)' : 'var(--color-success)'
                                }}
                              ></div>
                            </div>
                            <span className="progress-text">{link.use_count} / {link.max_uses} uses</span>
                          </div>
                        )}
                        
                        <div className="link-stats">
                          {!link.max_uses && <span>📊 Used {link.use_count} times</span>}
                          {link.expires_at && (
                            <span>📅 Expires: {formatDate(link.expires_at)}</span>
                          )}
                          {!link.expires_at && !link.max_uses && (
                            <span>♾️ No expiration</span>
                          )}
                          {link.require_email_verification && (
                            <span>✉️ Email verification required</span>
                          )}
                        </div>
                        {link.require_email_verification && link.verified_guest_emails && Object.keys(link.verified_guest_emails).length > 0 && (
                          <div className="verified-emails" style={{ marginTop: '8px', fontSize: '12px' }}>
                            <strong>Verified guests ({Object.keys(link.verified_guest_emails).length}):</strong>
                            <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {Object.keys(link.verified_guest_emails).map(email => (
                                <span key={email} style={{ 
                                  background: '#e8f5e9', 
                                  color: '#2e7d32', 
                                  padding: '2px 8px', 
                                  borderRadius: '12px',
                                  fontSize: '11px'
                                }}>
                                  {email}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {link.last_used_at && (
                          <span className="last-used">
                            🕐 Last used: {formatDate(link.last_used_at)}
                          </span>
                        )}
                      </div>
                      <div className="link-actions">
                        {/* Renew button - shown for expired or expiring-soon links */}
                        {link.is_active && (isExpired || isExpiringSoon) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRenewLink(link.id);
                            }}
                            className="renew-btn"
                            title="Renew for 30 days"
                            disabled={loading}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="23 4 23 10 17 10"></polyline>
                              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                            </svg>
                            Renew
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(linkUrl);
                          }}
                          className="copy-btn"
                          title="Copy link"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                          Copy
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLink(link.id);
                          }}
                          className="delete-btn"
                          title="Delete link"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </section>
          </div>
        )}

        {/* Share with Group View */}
        {view === 'share-group' && (
          <div className="modal-body">
            {groups.length === 0 ? (
              <div className="empty-state">
                <p>You don't have any groups yet.</p>
                <p className="hint">Create a group in the Sharing section first.</p>
              </div>
            ) : (
              <div className="share-group-form">
                <div className="form-group">
                  <div className="form-label">Select Group</div>
                  <select
                    value={selectedGroup?.id || ''}
                    onChange={(e) => {
                      const group = groups.find(g => g.id === parseInt(e.target.value));
                      setSelectedGroup(group || null);
                    }}
                  >
                    <option value="">Select a group...</option>
                    {groups.map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name} ({group.member_count} members)
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <div className="form-label">Permission</div>
                  <select
                    value={sharePermission}
                    onChange={(e) => setSharePermission(e.target.value as 'view' | 'edit' | 'full_edit')}
                  >
                    <option value="view">View only - Can see items</option>
                    <option value="edit">Can contribute - Can add & manage their own items</option>
                    <option value="full_edit">Full access - Can add and edit all items</option>
                  </select>
                </div>

                <div className="form-actions">
                  <button
                    onClick={async () => {
                      await handleShareWithGroup();
                      setView('main');
                    }}
                    disabled={!selectedGroup || loading}
                    className="create-btn"
                  >
                    {loading ? 'Sharing...' : 'Share with Group'}
                  </button>
                  <button
                    onClick={() => setView('main')}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create Guest Link View */}
        {view === 'create-link' && (
          <div className="modal-body">
            {createdLink ? (
              <div className="link-created">
                <div className="success-icon">✓</div>
                <h3>Guest Link Created!</h3>
                <p>Share this link with anyone to collaborate</p>
                <div className="link-display">
                  <input
                    type="text"
                    value={createdLink}
                    readOnly
                    onClick={(e) => e.currentTarget.select()}
                  />
                  <button onClick={() => copyToClipboard(createdLink)}>
                    Copy
                  </button>
                </div>
                <div className="link-actions-bottom">
                  <button onClick={() => {
                    setCreatedLink(null);
                    setView('main');
                  }} className="done-btn">
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <div className="create-link-form">
                {isMarketplace ? (
                  <div className="form-group">
                    <div className="form-label">Permission</div>
                    <div style={{ padding: '8px 12px', background: 'var(--color-bg-tertiary)', borderRadius: '6px', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                      👁️ View only — Marketplace links are always read-only
                    </div>
                  </div>
                ) : (
                  <div className="form-group">
                    <div className="form-label">Permission</div>
                    <select
                      value={linkPermission}
                      onChange={(e) => setLinkPermission(e.target.value as 'view' | 'edit' | 'full_edit')}
                    >
                      <option value="view">View only - Can see items</option>
                      <option value="edit">Can contribute - Can add & manage their own items</option>
                      <option value="full_edit">Full access - Can add and edit all items</option>
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={linkExpires}
                      onChange={(e) => setLinkExpires(e.target.checked)}
                    />
                    <span>Set expiration date</span>
                  </label>
                  {linkExpires && (
                    <input
                      type="date"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  )}
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={maxUses !== null}
                      onChange={(e) => setMaxUses(e.target.checked ? 10 : null)}
                    />
                    <span>Limit number of uses</span>
                  </label>
                  {maxUses !== null && (
                    <input
                      type="number"
                      value={maxUses}
                      onChange={(e) => setMaxUses(parseInt(e.target.value) || 1)}
                      min="1"
                      placeholder="Max uses"
                    />
                  )}
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={requireEmailVerification}
                      onChange={(e) => setRequireEmailVerification(e.target.checked)}
                    />
                    <span>Require email verification</span>
                  </label>
                  {requireEmailVerification && (
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Guests must verify their email before accessing this collection. Useful for tracking workers in moving companies or professional settings.
                    </p>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    onClick={handleCreateGuestLink}
                    disabled={loading}
                    className="create-btn"
                  >
                    {loading ? 'Creating...' : 'Create Link'}
                  </button>
                  <button
                    onClick={() => setView('main')}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {/* Transfer to Client View */}
        {view === 'transfer' && (
          <div className="modal-body">
            {transferDone ? (
              <div className="link-created">
                <div className="success-icon">✓</div>
                <h3>Invitation Sent!</h3>
                <p>We've emailed <strong>{transferEmail}</strong> with a link to claim this collection.</p>
                <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginTop: '8px' }}>
                  The invitation expires in 7 days. Once accepted, {transferKeepCopy ? "you'll retain view-only access." : "you'll no longer have access to this collection."}
                </p>
                <div className="link-actions-bottom">
                  <button onClick={() => { setTransferDone(false); setView('main'); }} className="done-btn">
                    Done
                  </button>
                </div>
              </div>
            ) : pendingTransfer ? (
              <div className="create-link-form">
                <div style={{ padding: '12px', background: 'var(--color-bg-tertiary)', borderRadius: '8px', marginBottom: '16px' }}>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Pending Transfer</p>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                    Invitation sent to <strong>{pendingTransfer.to_email}</strong>
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                    Expires {new Date(pendingTransfer.expires_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="form-actions">
                  <button onClick={handleCancelTransfer} disabled={loading} className="delete-btn" style={{ width: '100%' }}>
                    {loading ? 'Cancelling...' : 'Cancel Invitation'}
                  </button>
                </div>
                {error && <p style={{ color: 'var(--color-danger)', fontSize: '13px', marginTop: '8px' }}>{error}</p>}
              </div>
            ) : (
              <div className="create-link-form">
                <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>
                  Transfer ownership of <strong>"{collection.name}"</strong> to your client. They'll receive an email invitation to create a free account and claim the collection.
                </p>

                <div className="form-group">
                  <div className="form-label">Client's Email Address</div>
                  <input
                    type="email"
                    value={transferEmail}
                    onChange={(e) => setTransferEmail(e.target.value)}
                    placeholder="client@example.com"
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <div className="form-label">Personal Message (optional)</div>
                  <textarea
                    value={transferMessage}
                    onChange={(e) => setTransferMessage(e.target.value)}
                    placeholder="e.g. Hi! Your move inventory is complete and ready for you to claim."
                    rows={3}
                    style={{ width: '100%', resize: 'vertical', padding: '8px', borderRadius: '6px', border: '1px solid var(--color-border)', fontFamily: 'inherit', fontSize: '14px' }}
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={transferKeepCopy}
                      onChange={(e) => setTransferKeepCopy(e.target.checked)}
                    />
                    <span>Keep a view-only copy for my records</span>
                  </label>
                  <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
                    {transferKeepCopy
                      ? 'After transfer, you can still view this collection but not edit it.'
                      : 'After transfer, you will lose access to this collection.'}
                  </p>
                </div>

                {error && <p style={{ color: 'var(--color-danger)', fontSize: '13px', marginBottom: '8px' }}>{error}</p>}

                <div className="form-actions">
                  <button
                    onClick={handleInitiateTransfer}
                    disabled={loading || !transferEmail.trim()}
                    className="create-btn"
                  >
                    {loading ? 'Sending...' : 'Send Transfer Invitation'}
                  </button>
                  <button onClick={() => setView('main')} className="cancel-btn">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </BottomSheet>
  );
};

export default CollectionShareModal;
